/* eslint-disable react/jsx-no-useless-fragment */

import * as React from "react";
import { Col, FormGroup, Row } from "react-bootstrap";

import { strategyGroupCategoryLabels, strategyTargetGroupLabels } from "../../../../../../../label/activity";
import type { Choice, Option } from "../../../../../../../react-components/lu-component/src/index";
import { Radios } from "../../../../../../../react-components/lu-component/src/index";

import {
	BeautyType,
	beautyTypeOptions,
	EyeshadowType,
	eyeshaowTypeOptions,
	FacewashFunction,
	facewashFunctionOptions,
	FoundationType,
	foundationTypeOptions,
	Gypsy,
	GypsyChoice,
	LipstickType,
	lipstickTypeOptions,
	StrategyGroup,
} from "../../../../../../../server/models/activity";
import { canSetFacewachFunction, isCleansing, isFaceWash } from "../../../../../../../server/models/category";
import type { SurveyChoice } from "../../../../../../../server/models/shared";
import type { TrueAdCopyStrategyGroup } from "../../../../../types/ad-copy/activity";
import { AccordionForm } from "../../../../parts/accodion-form";
import { emptyToUndefined, isSkincare } from "./utils";

const key = "gypsy";

type Key = typeof key;

type Value = TrueAdCopyStrategyGroup[Key];

type Props = {
	onChange: (name: Key, value: Value) => void;
	strategyGroup: StrategyGroup;
	surveyChoiceOptions: Option[];
};

export const GypsyForms = React.memo((props: Props) => {
	const { onChange, strategyGroup, surveyChoiceOptions } = props;

	const strategyGroupCategories = strategyGroup.category;

	const gypsy = strategyGroup.gypsy;

	const selectedStrategyGroupCategory = React.useMemo(
		() => strategyGroupCategories?.find((c) => c.surveyChoice === gypsy?.surveyChoice.value),
		[gypsy?.surveyChoice.value, strategyGroupCategories],
	);

	return (
		<Row>
			<FormGroup as={Col}>
				<AccordionForm defaultShow={gypsy != null} eventKey={key} title={strategyTargetGroupLabels.gypsy ?? ""}>
					{strategyGroupCategories != null && 0 < strategyGroupCategories.length ? (
						<>
							<Col md={12}>
								下記指定カテゴリで「
								{
									GypsyChoice[
										GypsyChoice["決まった商品・ブランドはなく、その時々でより効果がありそうなものを探して選んでいる"]
									]
								}
								」人を条件設定
							</Col>

							<Col>
								<Radios
									checked={gypsy?.surveyChoice.value ?? ""}
									choices={[
										...surveyChoiceOptions.filter((x) =>
											strategyGroupCategories.some((y) => y.surveyChoice === x.value),
										),
										{ label: "問わない", value: "" },
									]}
									name="gypsy-surveyChoice"
									onChange={(value) => {
										if (value === "") {
											onChange("gypsy", undefined);

											return;
										}

										onChange("gypsy", {
											gypsyChoices: [
												GypsyChoice[
													"決まった商品・ブランドはなく、その時々でより効果がありそうなものを探して選んでいる"
												].toString() as keyof typeof GypsyChoice,
											],
											surveyChoice: surveyChoiceOptions.find((x) => x.value === value) as SurveyChoice,
										} satisfies Gypsy);
									}}
								/>
							</Col>

							{gypsy != null && selectedStrategyGroupCategory != null && (
								<Row className="mt-4 px-3">
									{/* カテゴリタイプ（スキンケアのみ） */}
									{/* 美類タイプ */}
									{isSkincare(selectedStrategyGroupCategory.category.type) && (
										<Col md={3}>
											{strategyGroupCategoryLabels.beautyType}

											<Radios
												checked={gypsy.beautyType ?? ""}
												choices={[
													...(beautyTypeOptions.filter(
														(x) =>
															selectedStrategyGroupCategory.beautyType?.includes(x.value as keyof typeof BeautyType) &&
															x.label !== "その他",
													) as Choice[]),
													{ label: "問わない", value: "" },
												]}
												name="gypsy-beautyType"
												onChange={(value) => {
													onChange("gypsy", {
														...gypsy,
														beautyType: emptyToUndefined(value as keyof typeof BeautyType),
													} satisfies Gypsy);
												}}
											/>
										</Col>
									)}

									{/* 洗顔機能 */}
									{canSetFacewachFunction(selectedStrategyGroupCategory.category) && (
										<Col>
											{strategyTargetGroupLabels.facewashFunction}

											<Radios
												checked={gypsy.facewashFunction ?? ""}
												choices={[
													...(facewashFunctionOptions.filter(
														(x) =>
															x.value === strategyGroup.facewashFunction &&
															(x.value === FacewashFunction.洗顔機能のみの商品.toString()
																? isFaceWash(selectedStrategyGroupCategory.category)
																: true) &&
															(x.value === FacewashFunction.メイク落とし機能のみの商品.toString()
																? isCleansing(selectedStrategyGroupCategory.category)
																: true),
													) as Choice[]),
													{ label: "問わない", value: "" },
												]}
												name="gypsy-facewashFunction"
												onChange={(value) => {
													onChange("gypsy", {
														...gypsy,
														facewashFunction: emptyToUndefined(value as keyof typeof FacewashFunction),
													} satisfies Gypsy);
												}}
											/>
										</Col>
									)}

									{/* ファンデーションタイプ */}
									{selectedStrategyGroupCategory.category.type === "liquid-foundation" && (
										<Col>
											{strategyTargetGroupLabels.foundationType}

											<Radios
												checked={gypsy.foundationType ?? ""}
												choices={[
													...(foundationTypeOptions.filter(
														(x) => strategyGroup.foundationType?.includes(x.value as keyof typeof FoundationType),
													) as Choice[]),
													{ label: "問わない", value: "" },
												]}
												name="gypsy-foundationType"
												onChange={(value) => {
													onChange("gypsy", {
														...gypsy,
														foundationType: emptyToUndefined(value as keyof typeof FoundationType),
													} satisfies Gypsy);
												}}
											/>
										</Col>
									)}

									{/* 口紅・リップグロス使用カテゴリ */}
									{selectedStrategyGroupCategory.category.type === "lipstick" && (
										<Col>
											{strategyTargetGroupLabels.lipstickType}

											<Radios
												checked={gypsy.lipstickType ?? ""}
												choices={[
													...(lipstickTypeOptions.filter(
														(x) => strategyGroup.lipstickType?.includes(x.value as keyof typeof LipstickType),
													) as Choice[]),
													{ label: "問わない", value: "" },
												]}
												name="gypsy-lipstickType"
												onChange={(value) => {
													onChange("gypsy", {
														...gypsy,
														lipstickType: emptyToUndefined(value as keyof typeof LipstickType),
													} satisfies Gypsy);
												}}
											/>
										</Col>
									)}

									{/* アイシャドー使用カテゴリ */}
									{selectedStrategyGroupCategory.category.type === "eyeShadow" && (
										<Col>
											{strategyTargetGroupLabels.eyeshadowType}

											<Radios
												checked={gypsy.eyeshadowType ?? ""}
												choices={[
													...(eyeshaowTypeOptions.filter(
														(x) => strategyGroup.eyeshadowType?.includes(x.value as keyof typeof EyeshadowType),
													) as Choice[]),
													{ label: "問わない", value: "" },
												]}
												name="gypsy-eyeshadowType"
												onChange={(value) => {
													onChange("gypsy", {
														...gypsy,
														eyeshadowType: emptyToUndefined(value as keyof typeof EyeshadowType),
													} satisfies Gypsy);
												}}
											/>
										</Col>
									)}
								</Row>
							)}
						</>
					) : (
						<></>
					)}
				</AccordionForm>
			</FormGroup>
		</Row>
	);
});
